import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { setSessionLostHandler } from '../api/client';
import { authApi } from '../api/endpoints';
import { ApiError } from '../api/errors';
import type { AuthResponse, User } from '../api/types';
import { clearTokens, loadTokens, saveTokens } from './tokenStore';

type Status = 'loading' | 'authenticated' | 'anonymous';

export type SessionLostReason = 'expired' | 'reuse';

interface AuthValue {
  status: Status;
  user: User | null;
  /** Set when a session ended on its own, so the login screen can explain why. */
  sessionLost: SessionLostReason | null;
  login: (email: string, password: string) => Promise<void>;
  /** Troca o idToken do Google por uma sessão. Quem decide a identidade é o servidor. */
  loginWithGoogle: (idToken: string) => Promise<void>;
  register: (input: { email: string; password: string; displayName: string }) => Promise<void>;
  logout: () => Promise<void>;
  /** Refreshes the cached user after a profile edit or a handicap recalculation. */
  reloadUser: () => Promise<void>;
  clearSessionLost: () => void;
  /**
   * Set when the session could not be validated for a reason that is *not* an auth failure —
   * offline, rate limited, server down. The session is still presumed valid; the app just
   * could not confirm it yet.
   */
  bootError: unknown;
  retryBoot: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>(() => (loadTokens() ? 'loading' : 'anonymous'));
  const [user, setUser] = useState<User | null>(null);
  const [sessionLost, setSessionLost] = useState<SessionLostReason | null>(null);
  const [bootError, setBootError] = useState<unknown>(null);
  const [bootAttempt, setBootAttempt] = useState(0);
  const retryTimer = useRef<number | null>(null);

  // The HTTP client cannot import React state, so it calls back into it here.
  useEffect(() => {
    setSessionLostHandler((reason) => {
      setUser(null);
      setStatus('anonymous');
      setSessionLost(reason);
    });
  }, []);

  // Bootstrap: we have tokens from a previous visit — are they still good?
  useEffect(() => {
    if (status !== 'loading') return;
    let cancelled = false;

    authApi
      .me()
      .then((me) => {
        if (cancelled) return;
        setBootError(null);
        setUser(me);
        setStatus('authenticated');
      })
      .catch((err: unknown) => {
        if (cancelled) return;

        /**
         * Distinguish "not signed in" from "could not reach the server". Only a 401 means
         * the stored session is dead — the HTTP client has already tried to refresh it and
         * cleared storage itself. A rate limit or a network failure must leave the tokens
         * alone, or a bad minute of connectivity signs the user out of a session that is
         * supposed to last a month.
         */
        const isAuthFailure = err instanceof ApiError && err.isUnauthorized;
        if (isAuthFailure) {
          clearTokens();
          setUser(null);
          setStatus('anonymous');
          return;
        }

        // Transient: keep the session, surface the problem, and try again shortly.
        // A 429 carries how long to wait; anything else backs off gently.
        setBootError(err);
        const waitMs =
          err instanceof ApiError && err.isRateLimited && err.retryAfter
            ? (err.retryAfter + 1) * 1000
            : Math.min(15_000, 2000 * 2 ** bootAttempt);
        const timer = window.setTimeout(() => {
          if (!cancelled) setBootAttempt((n) => n + 1);
        }, waitMs);
        retryTimer.current = timer;
      });

    return () => {
      cancelled = true;
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
    };
    // bootAttempt drives the retry: bumping it re-runs this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, bootAttempt]);

  const adopt = useCallback((res: AuthResponse) => {
    saveTokens(res.tokens);
    setUser(res.user);
    setStatus('authenticated');
    setSessionLost(null);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      adopt(await authApi.login({ email, password }));
    },
    [adopt],
  );

  /**
   * O mesmo `adopt` do login por senha: o Google só produz a prova de identidade, e a sessão
   * que o app passa a ter é a mesma em qualquer caminho de entrada.
   */
  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      adopt(await authApi.google(idToken));
    },
    [adopt],
  );

  const register = useCallback(
    async (input: { email: string; password: string; displayName: string }) => {
      adopt(await authApi.register(input));
    },
    [adopt],
  );

  const logout = useCallback(async () => {
    const tokens = loadTokens();
    if (tokens) {
      // The endpoint needs a *valid* access token as well as the refresh token. If ours has
      // expired the call 401s — which does not matter: the local session is what we are
      // clearing, and a failed revoke must never trap the user in a logged-in shell.
      await authApi.logout(tokens.refreshToken).catch(() => undefined);
    }
    clearTokens();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const reloadUser = useCallback(async () => {
    const me = await authApi.me();
    setUser(me);
  }, []);

  const clearSessionLost = useCallback(() => setSessionLost(null), []);

  const retryBoot = useCallback(() => {
    if (retryTimer.current) window.clearTimeout(retryTimer.current);
    setBootError(null);
    setBootAttempt((n) => n + 1);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      user,
      sessionLost,
      login,
      loginWithGoogle,
      register,
      logout,
      reloadUser,
      clearSessionLost,
      bootError,
      retryBoot,
    }),
    [
      status,
      user,
      sessionLost,
      login,
      loginWithGoogle,
      register,
      logout,
      reloadUser,
      clearSessionLost,
      bootError,
      retryBoot,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}

/** The authenticated user, for screens that only render behind the route guard. */
export function useCurrentUser(): User {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser usado fora de uma rota autenticada');
  return user;
}
